import { Link } from "react-router-dom";
import { Zap, Check, ArrowRight, FileText, Boxes, Users, UserCog, BarChart3, ShieldCheck } from "lucide-react";

const MODULES = [
  { icon: FileText, title: "Facturation & Devis", desc: "Créez, envoyez et encaissez vos factures en ligne, avec relances automatiques." },
  { icon: Boxes, title: "Inventaire & Stocks", desc: "Suivez vos produits et recevez des alertes de réapprovisionnement." },
  { icon: Users, title: "CRM & Prospects", desc: "Pilotez vos clients et votre pipeline commercial." },
  { icon: UserCog, title: "Ressources Humaines", desc: "Gérez vos employés et validez les demandes de congés." },
  { icon: BarChart3, title: "Rapports avancés", desc: "Analysez votre chiffre d'affaires par client, produit et période." },
  { icon: ShieldCheck, title: "Rôles & équipe", desc: "Invitez vos collaborateurs et choisissez leurs accès module par module." },
];

const BG = "https://images.unsplash.com/photo-1631193816258-28b44b21e78b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBvZmZpY2UlMjB3b3Jrc3BhY2V8ZW58MHx8fHwxNzg4OTExMDQ2fDA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Nav */}
      <header className="flex items-center justify-between border-b border-border px-6 py-4 md:px-12">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center bg-primary text-primary-foreground"><Zap size={18} strokeWidth={2.5} /></div>
          <span className="font-display text-xl font-bold">GestionPro</span>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/login" data-testid="landing-login" className="text-sm text-muted-foreground hover:text-foreground">Connexion</Link>
          <Link to="/login?mode=register" data-testid="landing-cta-nav" className="bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90">Essai gratuit</Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <img src={BG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 grid-noise" />
        <div className="relative mx-auto max-w-4xl px-6 py-24 text-center md:py-32">
          <div className="mb-6 inline-flex items-center gap-2 border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
            <Zap size={13} /> 1 mois offert, sans carte pour commencer
          </div>
          <h1 className="font-display text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl">
            GestionPro, l'ERP pas cher<br />pour la gestion d'entreprise.
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-base text-muted-foreground md:text-lg">
            Un logiciel ERP complet qui réunit la facturation, les devis, la gestion des stocks,
            le CRM, les ressources humaines et les paiements en ligne — dans une plateforme
            claire, rapide et sécurisée. Idéal pour les TPE et PME.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link to="/login?mode=register" data-testid="landing-cta-hero" className="flex items-center gap-2 bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90">
              Démarrer l'essai gratuit <ArrowRight size={16} />
            </Link>
            <Link to="/login" className="border border-border px-6 py-3 text-sm transition-colors hover:bg-accent">J'ai déjà un compte</Link>
          </div>
        </div>
      </section>

      {/* Modules */}
      <section className="mx-auto max-w-6xl px-6 py-20">
        <h2 className="mb-2 text-center font-display text-3xl font-bold">Une suite complète</h2>
        <p className="mb-12 text-center text-muted-foreground">Tout ce dont votre entreprise a besoin, prêt à l'emploi.</p>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {MODULES.map((m) => (
            <div key={m.title} className="border border-border bg-card p-6 transition-colors hover:border-primary/40">
              <div className="mb-4 flex h-10 w-10 items-center justify-center bg-secondary text-primary"><m.icon size={20} /></div>
              <h3 className="font-display text-lg font-bold">{m.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{m.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className="border-t border-border bg-card/40">
        <div className="mx-auto max-w-lg px-6 py-20 text-center">
          <h2 className="font-display text-3xl font-bold">Un ERP pas cher, un tarif simple</h2>
          <div className="mt-8 border border-border bg-card p-8">
            <div className="font-display text-5xl font-bold">100€<span className="text-lg font-normal text-muted-foreground">/mois</span></div>
            <p className="mt-1 text-xs uppercase tracking-wide text-muted-foreground">après 1 mois d'essai gratuit · sans engagement</p>
            <ul className="mx-auto mt-8 max-w-xs space-y-2 text-left">
              {["Tous les modules inclus", "Utilisateurs illimités", "Paiement des factures en ligne", "Relances & rapports automatiques", "Support par e-mail"].map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm"><Check size={16} className="text-primary" /> {f}</li>
              ))}
            </ul>
            <Link to="/login?mode=register" data-testid="landing-cta-pricing" className="mt-8 inline-flex w-full items-center justify-center gap-2 bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90">
              Créer mon entreprise <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-border px-6 py-8 text-center text-xs text-muted-foreground">
        © {new Date().getFullYear()} GestionPro — La gestion d'entreprise, unifiée.
      </footer>
    </div>
  );
}
